/**
 * Post-Signup Onboarding Wizard
 * Guides new users through avatar creation and bot personality setup
 */

import { supabase } from './supabase.js';
import { generateOpenClawConfig, getInstallCommand } from './openclaw-config.js';
import { createLivePreview, generateAvatar, generateAIAvatar } from './avatar-creator.js';

// Live preview instance (persists across step re-renders)
let livePreviewInstance = null;

/**
 * Initialize onboarding for a user
 * @param {Object} user - Supabase auth user object
 * @returns {Promise<void>} Resolves when onboarding is complete
 */
export async function initOnboarding(user) {
  // Check if onboarding is already complete
  let userData = null;
  try {
    const resp = await supabase.auth.getUser();
    userData = resp.data;
  } catch (e) {
    console.warn('⚠ Supabase getUser failed, checking localStorage:', e.message);
  }

  // Check Supabase metadata or localStorage fallback
  const savedMeta = localStorage.getItem('veridian_user_metadata');
  const localComplete = savedMeta && JSON.parse(savedMeta).onboarding_complete;

  if (userData?.user?.user_metadata?.onboarding_complete || localComplete) {
    console.log('✓ User has already completed onboarding');
    const resolvedUser = userData?.user || user;
    if (localComplete && !resolvedUser.user_metadata?.onboarding_complete) {
      resolvedUser.user_metadata = { ...resolvedUser.user_metadata, ...JSON.parse(savedMeta) };
    }
    dispatchOnboardingComplete(resolvedUser);
    return;
  }

  console.log('🎨 Starting onboarding wizard for:', user.email);

  // Show the wizard
  return new Promise((resolve) => {
    showOnboardingWizard(user, resolve);
  });
}

/**
 * Create and display the onboarding wizard modal
 */
function showOnboardingWizard(user, onComplete) {
  // Remove existing wizard if present
  const existing = document.getElementById('onboarding-wizard');
  if (existing) existing.remove();

  const wizard = document.createElement('div');
  wizard.id = 'onboarding-wizard';

  // Apply inline styles
  Object.assign(wizard.style, {
    position: 'fixed',
    top: '0',
    left: '0',
    width: '100%',
    height: '100%',
    backgroundColor: 'rgba(10, 21, 32, 0.95)',
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'center',
    zIndex: '10000',
    backdropFilter: 'blur(10px)',
    animation: 'fadeIn 0.3s ease-out',
    overflowY: 'auto',
    paddingTop: '20px',
    paddingBottom: '20px',
    boxSizing: 'border-box',
  });

  const container = document.createElement('div');
  Object.assign(container.style, {
    width: '90%',
    maxWidth: '600px',
    backgroundColor: '#152535',
    borderRadius: '12px',
    border: '1px solid rgba(212, 232, 237, 0.3)',
    padding: '36px',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)',
    animation: 'slideUp 0.3s ease-out',
    flexShrink: '0',
    marginTop: 'auto',
    marginBottom: 'auto',
  });

  wizard.appendChild(container);

  // Create step container
  const stepsContainer = document.createElement('div');
  stepsContainer.id = 'onboarding-steps';
  container.appendChild(stepsContainer);

  // Create wizard state
  const wizardState = {
    currentStep: 1,
    avatarDescription: '',
    avatarUrl: null,
    botName: '',
    botPersonality: '',
    botPreset: null,
    botTone: 3,
    botResponseLength: 3,
    botValues: ''
  };

  // Attach handlers
  const wizardData = {
    user,
    state: wizardState,
    container,
    stepsContainer,
    wizard,
    onComplete
  };

  // Add styles
  injectOnboardingStyles();

  // Show step 1
  showStep1(wizardData);

  document.body.appendChild(wizard);
}

/**
 * Step 1: Create Your Avatar
 */
function showStep1(data) {
  const { stepsContainer, state, container } = data;
  stepsContainer.innerHTML = '';

  // Header
  const header = document.createElement('div');
  Object.assign(header.style, {
    marginBottom: '32px',
    textAlign: 'center'
  });

  const title = document.createElement('h2');
  title.textContent = 'Create Your Avatar';
  Object.assign(title.style, {
    fontFamily: 'Cinzel, serif',
    fontSize: '28px',
    color: '#d4e8ed',
    marginBottom: '12px',
    letterSpacing: '1px'
  });
  header.appendChild(title);

  const subtitle = document.createElement('p');
  subtitle.textContent = 'Step 1 of 3: Describe your digital presence';
  Object.assign(subtitle.style, {
    fontFamily: 'Cormorant Garamond, serif',
    fontSize: '14px',
    color: 'rgba(212, 232, 237, 0.7)',
    margin: '0'
  });
  header.appendChild(subtitle);

  stepsContainer.appendChild(header);

  // Input section
  const inputSection = document.createElement('div');
  Object.assign(inputSection.style, {
    marginBottom: '24px'
  });

  const label = document.createElement('label');
  label.textContent = 'Avatar Description';
  Object.assign(label.style, {
    display: 'block',
    fontFamily: 'Cormorant Garamond, serif',
    fontSize: '14px',
    color: '#d4e8ed',
    marginBottom: '8px',
    fontWeight: '500'
  });
  inputSection.appendChild(label);

  const textarea = document.createElement('textarea');
  textarea.placeholder = 'e.g., "A warrior angel with golden wings and silver armor"';
  textarea.value = state.avatarDescription;
  Object.assign(textarea.style, {
    width: '100%',
    minHeight: '100px',
    padding: '12px',
    fontFamily: 'Cormorant Garamond, serif',
    fontSize: '14px',
    color: '#d4e8ed',
    backgroundColor: 'rgba(10, 21, 32, 0.5)',
    border: '1px solid rgba(212, 232, 237, 0.3)',
    borderRadius: '6px',
    boxSizing: 'border-box',
    resize: 'vertical'
  });
  textarea.addEventListener('input', (e) => {
    state.avatarDescription = e.target.value;
  });
  inputSection.appendChild(textarea);

  stepsContainer.appendChild(inputSection);

  // 3D Avatar Preview
  const previewSection = document.createElement('div');
  Object.assign(previewSection.style, {
    marginBottom: '24px',
    textAlign: 'center'
  });

  const previewLabel = document.createElement('p');
  previewLabel.textContent = state.avatarUrl ? 'Your 3D Avatar (drag to rotate)' : 'Preview will appear here';
  Object.assign(previewLabel.style, {
    fontFamily: 'Cormorant Garamond, serif',
    fontSize: '12px',
    color: 'rgba(212, 232, 237, 0.7)',
    marginBottom: '12px'
  });
  previewSection.appendChild(previewLabel);

  const previewContainer = document.createElement('div');
  previewContainer.id = 'avatar-3d-preview';
  Object.assign(previewContainer.style, {
    width: '280px',
    height: '280px',
    margin: '0 auto',
    borderRadius: '8px',
    border: '1px solid rgba(212, 232, 237, 0.3)',
    overflow: 'hidden',
    backgroundColor: 'rgba(10, 21, 32, 0.8)',
  });
  previewSection.appendChild(previewContainer);

  stepsContainer.appendChild(previewSection);

  // Mount/update live preview after DOM insertion
  requestAnimationFrame(() => {
    const el = document.getElementById('avatar-3d-preview');
    if (!el) return;

    if (state.avatarDescription.trim()) {
      if (livePreviewInstance) {
        // Re-mount in new container
        livePreviewInstance.dispose();
        livePreviewInstance = null;
      }
      livePreviewInstance = createLivePreview(el, state.avatarDescription);
    } else if (livePreviewInstance) {
      livePreviewInstance.dispose();
      livePreviewInstance = null;
    }
  });

  // Generate / Update button
  const generateBtn = document.createElement('button');
  generateBtn.textContent = state.avatarUrl ? 'Regenerate Avatar' : 'Generate Avatar';
  Object.assign(generateBtn.style, {
    width: '100%',
    padding: '12px',
    marginBottom: '16px',
    fontFamily: 'Cormorant Garamond, serif',
    fontSize: '14px',
    color: '#152535',
    backgroundColor: '#d4e8ed',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontWeight: '500',
    transition: 'all 0.2s'
  });
  generateBtn.addEventListener('mouseover', () => {
    generateBtn.style.backgroundColor = '#ffffff';
    generateBtn.style.transform = 'translateY(-2px)';
  });
  generateBtn.addEventListener('mouseout', () => {
    generateBtn.style.backgroundColor = '#d4e8ed';
    generateBtn.style.transform = 'none';
  });
  generateBtn.addEventListener('click', async () => {
    if (!state.avatarDescription.trim()) {
      showMessage('Please describe your avatar first', 'error');
      return;
    }
    generateBtn.disabled = true;
    generateBtn.textContent = 'Generating AI Avatar...';

    try {
      // First update the 3D preview immediately (fast feedback)
      const el = document.getElementById('avatar-3d-preview');
      if (livePreviewInstance) {
        livePreviewInstance.updateDescription(state.avatarDescription);
      } else if (el) {
        livePreviewInstance = createLivePreview(el, state.avatarDescription);
      }

      // Then call the AI image generation API (DALL-E 3)
      const result = await generateAIAvatar(state.avatarDescription);
      state.avatarUrl = result.imageUrl;
      state._avatarSource = result.source;

      // If we got an AI image, show it below the 3D preview
      if (result.source === 'dalle') {
        let aiPreview = document.getElementById('ai-avatar-preview');
        if (!aiPreview) {
          aiPreview = document.createElement('div');
          aiPreview.id = 'ai-avatar-preview';
          Object.assign(aiPreview.style, {
            textAlign: 'center',
            marginTop: '16px',
          });
          const aiLabel = document.createElement('p');
          aiLabel.textContent = 'AI Generated Portrait';
          Object.assign(aiLabel.style, {
            fontFamily: 'Cormorant Garamond, serif',
            fontSize: '12px',
            color: 'rgba(212, 232, 237, 0.7)',
            marginBottom: '8px',
          });
          aiPreview.appendChild(aiLabel);
          const aiImg = document.createElement('img');
          aiImg.id = 'ai-avatar-img';
          Object.assign(aiImg.style, {
            maxWidth: '200px',
            height: 'auto',
            borderRadius: '8px',
            border: '1px solid rgba(212, 232, 237, 0.3)',
          });
          aiPreview.appendChild(aiImg);
          // Insert after the 3D preview section
          const previewParent = el?.parentElement?.parentElement;
          if (previewParent) {
            previewParent.insertBefore(aiPreview, generateBtn);
          }
        }
        const aiImg = document.getElementById('ai-avatar-img');
        if (aiImg) aiImg.src = result.imageUrl;
      }

      previewLabel.textContent = 'Your 3D Avatar (drag to rotate)';
      generateBtn.disabled = false;
      generateBtn.textContent = 'Regenerate Avatar';
    } catch (error) {
      console.error('Avatar generation failed:', error);
      showMessage('Avatar generation failed. Try again.', 'error');
      generateBtn.disabled = false;
      generateBtn.textContent = 'Generate Avatar';
    }
  });
  stepsContainer.appendChild(generateBtn);

  // Navigation buttons
  const navContainer = document.createElement('div');
  Object.assign(navContainer.style, {
    display: 'flex',
    gap: '12px',
    justifyContent: 'space-between'
  });

  const nextBtn = document.createElement('button');
  nextBtn.textContent = 'Next: Bot Personality →';
  applyPrimaryButtonStyle(nextBtn);
  nextBtn.addEventListener('click', () => {
    if (!state.avatarUrl) {
      showMessage('Please generate an avatar first', 'error');
      return;
    }
    showStep2(data);
  });
  navContainer.appendChild(nextBtn);

  stepsContainer.appendChild(navContainer);
}

/**
 * Step 2: Personalize Your Bot
 */
function showStep2(data) {
  const { stepsContainer, state } = data;
  stepsContainer.innerHTML = '';

  // Header
  const header = document.createElement('div');
  Object.assign(header.style, {
    marginBottom: '32px',
    textAlign: 'center'
  });

  const title = document.createElement('h2');
  title.textContent = 'Personalize Your Bot';
  Object.assign(title.style, {
    fontFamily: 'Cinzel, serif',
    fontSize: '28px',
    color: '#d4e8ed',
    marginBottom: '12px',
    letterSpacing: '1px'
  });
  header.appendChild(title);

  const subtitle = document.createElement('p');
  subtitle.textContent = 'Step 2 of 3: Define personality and communication style';
  Object.assign(subtitle.style, {
    fontFamily: 'Cormorant Garamond, serif',
    fontSize: '14px',
    color: 'rgba(212, 232, 237, 0.7)',
    margin: '0'
  });
  header.appendChild(subtitle);

  stepsContainer.appendChild(header);

  // Bot Name
  createFormField(stepsContainer, 'Bot Name', state, 'botName', 'text', 'e.g. Nova, Atlas, Sage...');

  // Personality Presets
  const presetSection = document.createElement('div');
  Object.assign(presetSection.style, {
    marginBottom: '24px'
  });

  const presetLabel = document.createElement('label');
  presetLabel.textContent = 'Personality Preset (optional)';
  Object.assign(presetLabel.style, {
    display: 'block',
    fontFamily: 'Cormorant Garamond, serif',
    fontSize: '14px',
    color: '#d4e8ed',
    marginBottom: '12px',
    fontWeight: '500'
  });
  presetSection.appendChild(presetLabel);

  const presets = ['Guardian Angel', 'Life Coach', 'Creative Muse', 'No-BS Advisor', 'Zen Master', 'Hype Friend'];
  const presetChips = document.createElement('div');
  Object.assign(presetChips.style, {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px'
  });

  presets.forEach(preset => {
    const chip = document.createElement('button');
    chip.textContent = preset;
    const isSelected = state.botPreset === preset;
    Object.assign(chip.style, {
      padding: '8px 16px',
      fontFamily: 'Cormorant Garamond, serif',
      fontSize: '13px',
      color: isSelected ? '#152535' : '#d4e8ed',
      backgroundColor: isSelected ? '#d4e8ed' : 'transparent',
      border: `1px solid ${isSelected ? '#d4e8ed' : 'rgba(212, 232, 237, 0.3)'}`,
      borderRadius: '20px',
      cursor: 'pointer',
      transition: 'all 0.2s'
    });
    chip.addEventListener('click', () => {
      state.botPreset = preset;
      showStep2(data);
    });
    chip.addEventListener('mouseover', () => {
      chip.style.backgroundColor = 'rgba(212, 232, 237, 0.1)';
    });
    chip.addEventListener('mouseout', () => {
      chip.style.backgroundColor = isSelected ? '#d4e8ed' : 'transparent';
    });
    presetChips.appendChild(chip);
  });

  presetSection.appendChild(presetChips);
  stepsContainer.appendChild(presetSection);

  // Personality Description
  const personaField = document.createElement('div');
  Object.assign(personaField.style, {
    marginBottom: '24px'
  });

  const personaLabel = document.createElement('label');
  personaLabel.textContent = 'Personality Description';
  Object.assign(personaLabel.style, {
    display: 'block',
    fontFamily: 'Cormorant Garamond, serif',
    fontSize: '14px',
    color: '#d4e8ed',
    marginBottom: '8px',
    fontWeight: '500'
  });
  personaField.appendChild(personaLabel);

  const personaTextarea = document.createElement('textarea');
  personaTextarea.placeholder = 'e.g., "Warm and witty, always encouraging, loves dad jokes"';
  personaTextarea.value = state.botPersonality;
  Object.assign(personaTextarea.style, {
    width: '100%',
    minHeight: '80px',
    padding: '12px',
    fontFamily: 'Cormorant Garamond, serif',
    fontSize: '14px',
    color: '#d4e8ed',
    backgroundColor: 'rgba(10, 21, 32, 0.5)',
    border: '1px solid rgba(212, 232, 237, 0.3)',
    borderRadius: '6px',
    boxSizing: 'border-box',
    resize: 'vertical'
  });
  personaTextarea.addEventListener('input', (e) => {
    state.botPersonality = e.target.value;
  });
  personaField.appendChild(personaTextarea);
  stepsContainer.appendChild(personaField);

  // Sliders: Tone
  createSlider(stepsContainer, 'Tone', state, 'botTone', 'Formal', 'Casual');

  // Sliders: Response Length
  createSlider(stepsContainer, 'Response Length', state, 'botResponseLength', 'Brief', 'Detailed');

  // Values
  createFormField(stepsContainer, 'Core Values/Principles', state, 'botValues', 'text', 'e.g., honesty, growth, kindness...');

  // Navigation
  const navContainer = document.createElement('div');
  Object.assign(navContainer.style, {
    display: 'flex',
    gap: '12px',
    justifyContent: 'space-between',
    marginTop: '32px'
  });

  const backBtn = document.createElement('button');
  backBtn.textContent = '← Back';
  applySecondaryButtonStyle(backBtn);
  backBtn.addEventListener('click', () => showStep1(data));
  navContainer.appendChild(backBtn);

  const nextBtn = document.createElement('button');
  nextBtn.textContent = 'Review → ';
  applyPrimaryButtonStyle(nextBtn);
  nextBtn.addEventListener('click', () => {
    if (!state.botName.trim()) {
      showMessage('Please give your bot a name', 'error');
      return;
    }
    showStep3(data);
  });
  navContainer.appendChild(nextBtn);

  stepsContainer.appendChild(navContainer);
}

/**
 * Step 3: Review & Launch
 */
function showStep3(data) {
  const { stepsContainer, state, user } = data;
  stepsContainer.innerHTML = '';

  // Header
  const header = document.createElement('div');
  Object.assign(header.style, {
    marginBottom: '32px',
    textAlign: 'center'
  });

  const title = document.createElement('h2');
  title.textContent = 'Review & Launch';
  Object.assign(title.style, {
    fontFamily: 'Cinzel, serif',
    fontSize: '28px',
    color: '#d4e8ed',
    marginBottom: '12px',
    letterSpacing: '1px'
  });
  header.appendChild(title);

  const subtitle = document.createElement('p');
  subtitle.textContent = 'Step 3 of 3: Confirm your settings and enter Veridian';
  Object.assign(subtitle.style, {
    fontFamily: 'Cormorant Garamond, serif',
    fontSize: '14px',
    color: 'rgba(212, 232, 237, 0.7)',
    margin: '0'
  });
  header.appendChild(subtitle);

  stepsContainer.appendChild(header);

  // Avatar preview
  const avatarPreview = document.createElement('div');
  Object.assign(avatarPreview.style, {
    textAlign: 'center',
    marginBottom: '32px',
    paddingBottom: '24px',
    borderBottom: '1px solid rgba(212, 232, 237, 0.3)'
  });

  // Show 3D preview in review
  const reviewPreviewContainer = document.createElement('div');
  reviewPreviewContainer.id = 'avatar-3d-review';
  Object.assign(reviewPreviewContainer.style, {
    width: '180px',
    height: '180px',
    margin: '0 auto 12px auto',
    borderRadius: '8px',
    border: '1px solid rgba(212, 232, 237, 0.3)',
    overflow: 'hidden',
    backgroundColor: 'rgba(10, 21, 32, 0.8)',
  });
  avatarPreview.appendChild(reviewPreviewContainer);

  // Mount a small preview for review
  requestAnimationFrame(() => {
    const el = document.getElementById('avatar-3d-review');
    if (el && state.avatarDescription) {
      const reviewPreview = createLivePreview(el, state.avatarDescription);
      // Store for cleanup
      data._reviewPreview = reviewPreview;
    }
  });

  const avatarDesc = document.createElement('p');
  avatarDesc.textContent = state.avatarDescription;
  Object.assign(avatarDesc.style, {
    fontFamily: 'Cormorant Garamond, serif',
    fontSize: '13px',
    color: 'rgba(212, 232, 237, 0.7)',
    margin: '0'
  });
  avatarPreview.appendChild(avatarDesc);

  stepsContainer.appendChild(avatarPreview);

  // Bot summary
  const botSummary = document.createElement('div');
  Object.assign(botSummary.style, {
    backgroundColor: 'rgba(10, 21, 32, 0.5)',
    border: '1px solid rgba(212, 232, 237, 0.2)',
    borderRadius: '8px',
    padding: '20px',
    marginBottom: '32px'
  });

  const botNameDisplay = document.createElement('h3');
  botNameDisplay.textContent = state.botName;
  Object.assign(botNameDisplay.style, {
    fontFamily: 'Cinzel, serif',
    fontSize: '20px',
    color: '#d4e8ed',
    margin: '0 0 12px 0',
    letterSpacing: '0.5px'
  });
  botSummary.appendChild(botNameDisplay);

  const botDetails = document.createElement('div');
  Object.assign(botDetails.style, {
    fontFamily: 'Cormorant Garamond, serif',
    fontSize: '13px',
    color: 'rgba(212, 232, 237, 0.8)',
    lineHeight: '1.6'
  });

  if (state.botPreset) {
    const presetLine = document.createElement('p');
    presetLine.innerHTML = `<strong>Personality:</strong> ${state.botPreset}`;
    Object.assign(presetLine.style, { margin: '0 0 8px 0' });
    botDetails.appendChild(presetLine);
  }

  if (state.botPersonality) {
    const personaLine = document.createElement('p');
    personaLine.innerHTML = `<strong>Character:</strong> ${state.botPersonality}`;
    Object.assign(personaLine.style, { margin: '0 0 8px 0' });
    botDetails.appendChild(personaLine);
  }

  const toneLine = document.createElement('p');
  toneLine.innerHTML = `<strong>Tone:</strong> ${getToneLabel(state.botTone)}`;
  Object.assign(toneLine.style, { margin: '0 0 8px 0' });
  botDetails.appendChild(toneLine);

  const lengthLine = document.createElement('p');
  lengthLine.innerHTML = `<strong>Response Style:</strong> ${getLengthLabel(state.botResponseLength)}`;
  Object.assign(lengthLine.style, { margin: '0 0 8px 0' });
  botDetails.appendChild(lengthLine);

  if (state.botValues) {
    const valuesLine = document.createElement('p');
    valuesLine.innerHTML = `<strong>Values:</strong> ${state.botValues}`;
    Object.assign(valuesLine.style, { margin: '0' });
    botDetails.appendChild(valuesLine);
  }

  botSummary.appendChild(botDetails);
  stepsContainer.appendChild(botSummary);

  // Navigation
  const navContainer = document.createElement('div');
  Object.assign(navContainer.style, {
    display: 'flex',
    gap: '12px',
    justifyContent: 'space-between',
    marginTop: '32px'
  });

  const backBtn = document.createElement('button');
  backBtn.textContent = '← Back';
  applySecondaryButtonStyle(backBtn);
  backBtn.addEventListener('click', () => showStep2(data));
  navContainer.appendChild(backBtn);

  const launchBtn = document.createElement('button');
  launchBtn.textContent = 'Enter Veridian →';
  launchBtn.setAttribute('data-action', 'launch');
  applyPrimaryButtonStyle(launchBtn);
  Object.assign(launchBtn.style, {
    minHeight: '48px',        // proper touch target
    touchAction: 'manipulation', // prevent double-tap zoom delay
    WebkitTapHighlightColor: 'transparent',
    cursor: 'pointer'
  });
  let launching = false;
  const handleLaunch = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (launching || launchBtn.disabled) return;
    launching = true;
    console.log('🚀 Enter Veridian tapped, event:', e.type);
    await completeOnboarding(data);
    launching = false;
  };
  launchBtn.addEventListener('click', handleLaunch);
  launchBtn.addEventListener('touchend', handleLaunch);
  navContainer.appendChild(launchBtn);

  stepsContainer.appendChild(navContainer);
}

/**
 * Complete onboarding and save to Supabase
 */
async function completeOnboarding(data) {
  const { user, state, wizard, onComplete } = data;

  const btn = wizard.querySelector('[data-action="launch"]') ||
              Array.from(wizard.querySelectorAll('button')).find(b => b.textContent.includes('Enter Veridian'));

  if (btn) {
    btn.disabled = true;
    btn.textContent = 'Launching...';
  }

  try {
    // Generate OpenClaw config
    const configs = generateOpenClawConfig({
      botName: state.botName,
      personality: state.botPersonality,
      tone: state.botTone,
      responseLength: state.botResponseLength,
      values: state.botValues,
      preset: state.botPreset,
      userName: user.user_metadata?.display_name || user.email,
      avatarUrl: state.avatarUrl
    });

    // Build the full metadata object
    const metadata = {
      ...user.user_metadata,
      onboarding_complete: true,
      bot_name: state.botName,
      bot_personality: state.botPersonality,
      bot_tone: state.botTone,
      bot_response_length: state.botResponseLength,
      bot_values: state.botValues,
      bot_preset: state.botPreset,
      avatar_description: state.avatarDescription,
      avatar_url: state.avatarUrl,
      openclaw_soul: configs.soul,
      openclaw_user: configs.user,
      openclaw_heartbeat: configs.heartbeat
    };

    // Try to save to Supabase, but don't block on failure (timeout after 5s for mobile)
    try {
      const supabasePromise = supabase.auth.updateUser({ data: metadata });
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Supabase save timed out')), 5000)
      );
      const { error } = await Promise.race([supabasePromise, timeoutPromise]);
      if (error) throw error;
      console.log('✓ Onboarding saved to Supabase');
    } catch (supaErr) {
      console.warn('⚠ Supabase save failed, saving locally:', supaErr.message || supaErr);
      // Save to localStorage as fallback so the world can still load
      localStorage.setItem('veridian_user_metadata', JSON.stringify(metadata));
    }

    // Merge metadata onto user object so the rest of the app can use it
    user.user_metadata = { ...user.user_metadata, ...metadata };

    // Clean up 3D previews (wrap each in try/catch — mobile WebGL contexts may be lost)
    try {
      if (livePreviewInstance) {
        livePreviewInstance.dispose();
        livePreviewInstance = null;
      }
    } catch (disposeErr) {
      console.warn('⚠ Live preview dispose failed (safe to ignore):', disposeErr.message);
      livePreviewInstance = null;
    }
    try {
      if (data._reviewPreview) {
        data._reviewPreview.dispose();
        data._reviewPreview = null;
      }
    } catch (disposeErr) {
      console.warn('⚠ Review preview dispose failed (safe to ignore):', disposeErr.message);
      data._reviewPreview = null;
    }

    console.log('✓ Onboarding complete, launching world...');

    // Hide wizard with animation
    wizard.style.animation = 'fadeOut 0.3s ease-out forwards';
    setTimeout(() => {
      try {
        wizard.remove();
      } catch (_) {
        // wizard may already be gone
      }
      dispatchOnboardingComplete(user);
      onComplete();
    }, 300);
  } catch (error) {
    console.error('Onboarding completion failed:', error, error?.stack);
    // Show error prominently on mobile
    const errorMsg = `Launch failed: ${error.message || 'Unknown error'}. Tap to retry.`;
    showMessage(errorMsg, 'error');
    if (btn) {
      btn.disabled = false;
      btn.textContent = 'Enter Veridian →';
    }
  }
}

/**
 * Dispatch custom event when onboarding is complete
 */
function dispatchOnboardingComplete(user) {
  const onboardingData = {
    botName: user.user_metadata?.bot_name,
    botPersonality: user.user_metadata?.bot_personality,
    botTone: user.user_metadata?.bot_tone,
    botResponseLength: user.user_metadata?.bot_response_length,
    botValues: user.user_metadata?.bot_values,
    botPreset: user.user_metadata?.bot_preset,
    avatarUrl: user.user_metadata?.avatar_url,
    openclawConfig: {
      soul: user.user_metadata?.openclaw_soul,
      user: user.user_metadata?.openclaw_user,
      heartbeat: user.user_metadata?.openclaw_heartbeat
    }
  };

  window.dispatchEvent(new CustomEvent('onboardingComplete', {
    detail: onboardingData
  }));
}

/**
 * Helper: Create a text input field
 */
function createFormField(container, label, state, stateKey, type = 'text', placeholder = '') {
  const field = document.createElement('div');
  Object.assign(field.style, {
    marginBottom: '24px'
  });

  const lbl = document.createElement('label');
  lbl.textContent = label;
  Object.assign(lbl.style, {
    display: 'block',
    fontFamily: 'Cormorant Garamond, serif',
    fontSize: '14px',
    color: '#d4e8ed',
    marginBottom: '8px',
    fontWeight: '500'
  });
  field.appendChild(lbl);

  const input = document.createElement('input');
  input.type = type;
  input.placeholder = placeholder;
  input.value = state[stateKey];
  Object.assign(input.style, {
    width: '100%',
    padding: '12px',
    fontFamily: 'Cormorant Garamond, serif',
    fontSize: '14px',
    color: '#d4e8ed',
    backgroundColor: 'rgba(10, 21, 32, 0.5)',
    border: '1px solid rgba(212, 232, 237, 0.3)',
    borderRadius: '6px',
    boxSizing: 'border-box'
  });
  input.addEventListener('input', (e) => {
    state[stateKey] = e.target.value;
  });
  input.addEventListener('focus', () => {
    input.style.borderColor = 'rgba(212, 232, 237, 0.6)';
    input.style.backgroundColor = 'rgba(10, 21, 32, 0.7)';
  });
  input.addEventListener('blur', () => {
    input.style.borderColor = 'rgba(212, 232, 237, 0.3)';
    input.style.backgroundColor = 'rgba(10, 21, 32, 0.5)';
  });
  field.appendChild(input);

  container.appendChild(field);
}

/**
 * Helper: Create a slider control
 */
function createSlider(container, label, state, stateKey, leftLabel, rightLabel) {
  const field = document.createElement('div');
  Object.assign(field.style, {
    marginBottom: '24px'
  });

  const lbl = document.createElement('label');
  lbl.textContent = label;
  Object.assign(lbl.style, {
    display: 'block',
    fontFamily: 'Cormorant Garamond, serif',
    fontSize: '14px',
    color: '#d4e8ed',
    marginBottom: '8px',
    fontWeight: '500'
  });
  field.appendChild(lbl);

  const sliderWrapper = document.createElement('div');
  Object.assign(sliderWrapper.style, {
    display: 'flex',
    alignItems: 'center',
    gap: '12px'
  });

  const leftSpan = document.createElement('span');
  leftSpan.textContent = leftLabel;
  Object.assign(leftSpan.style, {
    fontFamily: 'Cormorant Garamond, serif',
    fontSize: '12px',
    color: 'rgba(212, 232, 237, 0.6)',
    minWidth: '50px'
  });
  sliderWrapper.appendChild(leftSpan);

  const slider = document.createElement('input');
  slider.type = 'range';
  slider.min = '1';
  slider.max = '5';
  slider.value = state[stateKey];
  Object.assign(slider.style, {
    flex: '1',
    height: '4px',
    borderRadius: '2px',
    backgroundColor: 'rgba(212, 232, 237, 0.2)',
    outline: 'none',
    cursor: 'pointer',
    WebkitAppearance: 'none',
    appearance: 'none'
  });
  slider.addEventListener('input', (e) => {
    state[stateKey] = parseInt(e.target.value);
  });
  sliderWrapper.appendChild(slider);

  const rightSpan = document.createElement('span');
  rightSpan.textContent = rightLabel;
  Object.assign(rightSpan.style, {
    fontFamily: 'Cormorant Garamond, serif',
    fontSize: '12px',
    color: 'rgba(212, 232, 237, 0.6)',
    minWidth: '50px',
    textAlign: 'right'
  });
  sliderWrapper.appendChild(rightSpan);

  field.appendChild(sliderWrapper);

  const valueDisplay = document.createElement('div');
  valueDisplay.textContent = `${state[stateKey]} / 5`;
  Object.assign(valueDisplay.style, {
    fontFamily: 'Cormorant Garamond, serif',
    fontSize: '12px',
    color: 'rgba(212, 232, 237, 0.5)',
    marginTop: '6px',
    textAlign: 'center'
  });
  field.appendChild(valueDisplay);

  slider.addEventListener('input', () => {
    valueDisplay.textContent = `${state[stateKey]} / 5`;
  });

  container.appendChild(field);
}

/**
 * Helper: Apply primary button styling
 */
function applyPrimaryButtonStyle(btn) {
  Object.assign(btn.style, {
    flex: '1',
    padding: '12px 24px',
    fontFamily: 'Cormorant Garamond, serif',
    fontSize: '14px',
    color: '#152535',
    backgroundColor: '#d4e8ed',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontWeight: '500',
    transition: 'all 0.2s'
  });
  btn.addEventListener('mouseover', () => {
    btn.style.backgroundColor = '#ffffff';
    btn.style.transform = 'translateY(-2px)';
  });
  btn.addEventListener('mouseout', () => {
    btn.style.backgroundColor = '#d4e8ed';
    btn.style.transform = 'none';
  });
}

/**
 * Helper: Apply secondary button styling
 */
function applySecondaryButtonStyle(btn) {
  Object.assign(btn.style, {
    flex: '1',
    padding: '12px 24px',
    fontFamily: 'Cormorant Garamond, serif',
    fontSize: '14px',
    color: '#d4e8ed',
    backgroundColor: 'transparent',
    border: '1px solid rgba(212, 232, 237, 0.3)',
    borderRadius: '6px',
    cursor: 'pointer',
    fontWeight: '500',
    transition: 'all 0.2s'
  });
  btn.addEventListener('mouseover', () => {
    btn.style.backgroundColor = 'rgba(212, 232, 237, 0.1)';
  });
  btn.addEventListener('mouseout', () => {
    btn.style.backgroundColor = 'transparent';
  });
}

/**
 * Helper: Show message
 */
function showMessage(message, type = 'info') {
  // You could show a toast notification here
  console.log(`[${type.toUpperCase()}] ${message}`);
}

/**
 * Helper: Get tone label
 */
function getToneLabel(tone) {
  const labels = {
    1: 'Very Formal',
    2: 'Formal',
    3: 'Balanced',
    4: 'Casual',
    5: 'Very Casual'
  };
  return labels[tone] || 'Balanced';
}

/**
 * Helper: Get response length label
 */
function getLengthLabel(length) {
  const labels = {
    1: 'Very Brief',
    2: 'Short',
    3: 'Moderate',
    4: 'Detailed',
    5: 'Comprehensive'
  };
  return labels[length] || 'Moderate';
}

/**
 * Inject global styles for the wizard
 */
function injectOnboardingStyles() {
  // Check if styles already injected
  if (document.getElementById('onboarding-styles')) return;

  const style = document.createElement('style');
  style.id = 'onboarding-styles';
  style.textContent = `
    @keyframes fadeIn {
      from {
        opacity: 0;
      }
      to {
        opacity: 1;
      }
    }

    @keyframes fadeOut {
      from {
        opacity: 1;
      }
      to {
        opacity: 0;
      }
    }

    @keyframes slideUp {
      from {
        opacity: 0;
        transform: translateY(30px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }

    input[type="range"] {
      -webkit-slider-thumb-appearance: none;
      -webkit-appearance: none;
      appearance: none;
    }

    input[type="range"]::-webkit-slider-thumb {
      -webkit-appearance: none;
      appearance: none;
      width: 16px;
      height: 16px;
      border-radius: 50%;
      background: #d4e8ed;
      cursor: pointer;
      transition: all 0.2s;
    }

    input[type="range"]::-webkit-slider-thumb:hover {
      background: #ffffff;
      box-shadow: 0 0 8px rgba(212, 232, 237, 0.4);
    }

    input[type="range"]::-moz-range-thumb {
      width: 16px;
      height: 16px;
      border-radius: 50%;
      background: #d4e8ed;
      cursor: pointer;
      border: none;
      transition: all 0.2s;
    }

    input[type="range"]::-moz-range-thumb:hover {
      background: #ffffff;
      box-shadow: 0 0 8px rgba(212, 232, 237, 0.4);
    }

    input[type="range"]:focus {
      outline: none;
    }
  `;
  document.head.appendChild(style);
}
